import { createTheme } from "@material-ui/core/styles";

// ─── Design tokens ────────────────────────────────────────────────
const FONT =
	'"Inter", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const brand = {
	main: "#059669", // emerald-600
	dark: "#047857",
	light: "#34D399",
	contrastText: "#ffffff",
};

const slate = {
	50: "#F8FAFC",
	100: "#F1F5F9",
	200: "#E2E8F0",
	300: "#CBD5E1",
	400: "#94A3B8",
	500: "#64748B",
	600: "#475569",
	700: "#334155",
	800: "#1E293B",
	900: "#0F172A",
	950: "#0B1220",
};

const RADIUS = 12;

// ─── Overrides compartilhados (claro/escuro) ──────────────────────
const buildOverrides = (dark) => ({
	MuiCssBaseline: {
		"@global": {
			body: {
				backgroundColor: dark ? slate[950] : slate[100],
				fontFamily: FONT,
				WebkitFontSmoothing: "antialiased",
			},
			"*::-webkit-scrollbar": { width: 8, height: 8 },
			"*::-webkit-scrollbar-thumb": {
				borderRadius: 8,
				backgroundColor: dark ? slate[700] : slate[300],
			},
			"*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
		},
	},
	MuiButton: {
		root: {
			textTransform: "none",
			fontWeight: 600,
			borderRadius: 10,
			padding: "8px 18px",
		},
		containedPrimary: {
			boxShadow: "0 1px 2px rgba(5,150,105,.35)",
			"&:hover": { boxShadow: "0 4px 12px rgba(5,150,105,.35)" },
		},
		outlined: { borderRadius: 10 },
	},
	MuiPaper: {
		rounded: { borderRadius: RADIUS },
		elevation1: {
			boxShadow: dark
				? "0 1px 3px rgba(0,0,0,.5)"
				: "0 1px 3px rgba(15,23,42,.07), 0 1px 2px rgba(15,23,42,.04)",
		},
	},
	MuiCard: {
		root: {
			borderRadius: RADIUS,
			border: `1px solid ${dark ? slate[800] : slate[200]}`,
			boxShadow: "none",
		},
	},
	MuiTab: {
		root: {
			textTransform: "none",
			fontWeight: 600,
			fontSize: "0.85rem",
			minWidth: 90,
			"@media (min-width:600px)": { minWidth: 90 },
		},
	},
	MuiTabs: {
		indicator: { height: 3, borderRadius: 3 },
	},
	MuiOutlinedInput: {
		root: { borderRadius: 10 },
	},
	MuiChip: {
		root: { fontWeight: 600, borderRadius: 8 },
	},
	MuiTableCell: {
		head: {
			fontWeight: 700,
			fontSize: "0.75rem",
			textTransform: "uppercase",
			letterSpacing: "0.05em",
			color: dark ? slate[400] : slate[500],
		},
		root: { borderBottom: `1px solid ${dark ? slate[800] : slate[200]}` },
	},
	MuiDialog: {
		paper: { borderRadius: 16 },
	},
	MuiDialogTitle: {
		root: { "& h2": { fontWeight: 700 } },
	},
	MuiTooltip: {
		tooltip: {
			borderRadius: 8,
			fontWeight: 500,
			backgroundColor: dark ? slate[700] : slate[800],
		},
	},
	MuiAppBar: {
		colorPrimary: {
			backgroundColor: dark ? slate[900] : "#ffffff",
			color: dark ? slate[100] : slate[800],
			boxShadow: "none",
			borderBottom: `1px solid ${dark ? slate[800] : slate[200]}`,
		},
	},
	MuiListItem: {
		root: { borderRadius: 10 },
	},
	MuiBadge: {
		badge: { fontWeight: 700 },
	},
	MuiAvatar: {
		colorDefault: {
			backgroundColor: dark ? slate[600] : slate[400],
		},
	},
});

const buildProps = () => ({
	MuiButtonBase: { disableRipple: false },
	MuiTextField: { variant: "outlined", size: "small" },
	MuiPaper: { elevation: 1 },
});

// ─── Fábrica de tema ──────────────────────────────────────────────
const buildTheme = (mode, locale) => {
	const dark = mode === "dark";

	return createTheme(
		{
			// usado por vários componentes legados do app
			scrollbarStyles: {
				"&::-webkit-scrollbar": { width: 8, height: 8 },
				"&::-webkit-scrollbar-thumb": {
					borderRadius: 8,
					backgroundColor: dark ? slate[700] : slate[300],
				},
			},
			// tokens custom acessíveis via theme.custom
			custom: {
				sidebarBg: slate[900],
				sidebarText: slate[300],
				sidebarActiveBg: "rgba(52, 211, 153, 0.12)",
				sidebarActiveText: brand.light,
				chatBg: dark ? slate[950] : "#EFEAE2",
				bubbleIn: dark ? slate[800] : "#ffffff",
				bubbleOut: dark ? "#054d3b" : "#D9FDD3",
				border: dark ? slate[800] : slate[200],
				surface: dark ? slate[900] : "#ffffff",
				surfaceHover: dark ? slate[800] : slate[100],
				textSecondary: dark ? slate[400] : slate[500],
			},
			palette: {
				type: dark ? "dark" : "light",
				primary: brand,
				secondary: { main: "#0EA5E9", contrastText: "#fff" },
				error: { main: "#EF4444" },
				warning: { main: "#F59E0B" },
				success: { main: "#10B981" },
				background: {
					default: dark ? slate[950] : slate[100],
					paper: dark ? slate[900] : "#ffffff",
				},
				text: {
					primary: dark ? slate[100] : slate[800],
					secondary: dark ? slate[400] : slate[500],
				},
				divider: dark ? slate[800] : slate[200],
			},
			typography: {
				fontFamily: FONT,
				h4: { fontWeight: 800, letterSpacing: "-0.02em" },
				h5: { fontWeight: 700, letterSpacing: "-0.02em" },
				h6: { fontWeight: 700, letterSpacing: "-0.01em" },
				subtitle1: { fontWeight: 600 },
				subtitle2: { fontWeight: 600 },
				button: { fontWeight: 600, textTransform: "none" },
			},
			shape: { borderRadius: RADIUS },
			overrides: buildOverrides(dark),
			props: buildProps(),
		},
		locale
	);
};

export default buildTheme;
